/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { User } from '../types';

interface PermissionContextValue {
  permissions: Set<string>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasAllPermissions: (...permissions: string[]) => boolean;
  isSuperAdmin: boolean;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

interface PermissionProviderProps {
  user: User | null;
  children: ReactNode;
}

export function PermissionProvider({ user, children }: PermissionProviderProps) {
  const value = useMemo<PermissionContextValue>(() => {
    const permissions = new Set(user?.permissions ?? []);
    const isSuperAdmin = user?.role === 'SuperAdmin';

    return {
      permissions,
      isSuperAdmin,
      hasPermission: (permission: string) => {
        if (isSuperAdmin) return true;
        return permissions.has(permission);
      },
      hasAnyPermission: (...perms: string[]) => {
        if (isSuperAdmin) return true;
        return perms.some(p => permissions.has(p));
      },
      hasAllPermissions: (...perms: string[]) => {
        if (isSuperAdmin) return true;
        return perms.every(p => permissions.has(p));
      },
    };
  }, [user?.permissions, user?.role]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions(): PermissionContextValue {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}
